import React from 'react';
import { RatingIcon } from '../RatingIcon/RatingIcon';
import { Avatar } from '../Avatar/Avatar';
import moment from 'moment';
import styled from 'styled-components';
import { getCurrentLocale } from 'helpers/i18n';

const StyledName = styled.div`
  font-weight: 500 !important;
`;

/**
 * Renders a lead temperature data cell on a specific column
 * @param {integer} text - lead temperature - possible values = [1, 2, 3]
 * @param {any} record - the full cell info (unused)
 * @param {integer} index - the integer used to add unique IDs to arrays of content in React
 */
export const formatTemperature = (text, record, index) => {
  return <RatingIcon key={index} rating={parseInt(text)} type="temperature" />;
};

/**
 * Renders a lead quality data cell on a specific column
 * @param {integer} text - lead quality - possible values = [1, 2, 3]
 * @param {any} record - the full cell info (unused)
 * @param {integer} index - the integer used to add unique IDs to arrays of content in React
 */
export const formatQuality = (text, record, index) => (
  <RatingIcon key={index} rating={parseInt(text)} type="quality" />
);

/**
 * Renders an Avatar data cell on a specific column
 * @param {string} text - Avatar name - e.g. "John Doe" will render an avatar with "JD" initials
 * @param {any} record - the full cell info (unused)
 * @param {integer} index - the integer used to add unique IDs to arrays of content in React
 */
export const formatAvatar = (text, record, index) => <Avatar key={index}>{text}</Avatar>;

/**
 * Renders a DateFromNow data cell on a specific column
 * @param {date} text - A JS date from the past
 * @param {any} record - the full cell info (unused)
 * @param {integer} index - the integer used to add unique IDs to arrays of content in React
 */
export const formatDateFromNow = (text, record, index) => {
  moment.locale(getCurrentLocale());
  return <span key={index}>{moment(text).fromNow()}</span>;
};

/**
 * Renders a Custom Name+Email data cell on a specific column
 * @param {nameAndEmail} text - An object containing a name and an email props = { name: 'John Doe', email: 'john@doe.com' }
 * @param {any} record - the full cell info (unused)
 * @param {integer} index - the integer used to add unique IDs to arrays of content in React
 */
export const formatNameAndEmail = (text, record, index) => (
  <div key={index}>
    <StyledName>{text.name}</StyledName>
    <span>{text.email}</span>
  </div>
);

/**
 * Allows to customize columns with a custom table cell renderer.
 * You can customize it by sending a custom column.dataType value
 * and by adding more cases in this function
 * @param {Array<ColumnProps>} columns - Antd 3.x column array - More info at https://3x.ant.design/components/table/#:~:text=3.4.2-,columns,-Columns%20of%20table
 */
export const formatColumns = (columns) =>
  columns.map((column) => {
    switch (column.dataType) {
      case 'temperature':
        return {
          ...column,
          render: formatTemperature,
        };
      case 'quality':
        return {
          ...column,
          render: formatQuality,
        };
      case 'avatar':
        return {
          ...column,
          render: formatAvatar,
        };
      case 'dateFromNow':
        return {
          ...column,
          render: formatDateFromNow,
        };
      case 'nameAndEmail':
        return {
          ...column,
          render: formatNameAndEmail,
        };
      default:
        // Renders a text-only default antd 3.x table cells for a specific column
        return column;
    }
  });

export const getSearchableColumns = (columns) => {
  return columns.filter((column) => column.searchable).map((column) => column.dataIndex);
};

export const searchDataSource = (dataSource, searchTerm, columns) => {
  if (!searchTerm) {
    return dataSource;
  }

  const searchableColumns = getSearchableColumns(columns);

  const searchRow = (row) =>
    Object.entries(row).some(([key, value]) => {
      // Skip search on columns that are not searchable
      if (!searchableColumns.includes(key)) {
        return false;
      }

      // True = flag row as 'searchTerm found at least in one column'
      // False = no searchTerm found in column
      return JSON.stringify(value).toLowerCase().includes(searchTerm.toLowerCase());
    });

  const rowsMatchingSearch = dataSource.filter(searchRow);

  return rowsMatchingSearch;
};
